/**
 * Type guard helper for filtering lists that may have empty items
 *
 * @example myList.filter(isDefined)
 *
 * @param input potentially undefined value
 */
export const isDefined = <T>(input: T | undefined | null): input is T =>
  typeof input !== 'undefined' && input !== null;

/**
 * Type guard helper for filtering lists that may have falsey items
 *
 * @example myList.filter(isTruthy)
 *
 * @param input potentially falsey value
 */
export const isTruthy = <T>(
  input: T | undefined | null | boolean,
): input is T =>
  typeof input !== 'undefined' && input !== null && input !== false;

/**
 * Guard version of `Object.prototype.hasOwnProperty` that makes it
 * possible to access the property being checked (as `unknown`)
 *
 * @param object object to test
 * @param property name of property to test for
 */
export const hasOwnProperty = <X extends {}, Y extends PropertyKey>(
  object: X,
  property: Y,
): object is X & Record<Y, unknown> =>
  Object.prototype.hasOwnProperty.call(object, property);

/**
 * Alternative version of `Array.prototype.includes` that allows testing for
 * the existence of an item with a type that is a _superset_ of the type of the
 * items in the array.
 *
 * This allows us to use it to check whether an item of type `string` exists in
 * an array of string literals (e.g: `['foo', 'bar'] as const`) without TypeScript
 * complaining. It will, however, throw a compiler error if you try to pass an item
 * of type `number`.
 *
 * @example
 * const things = ['foo', 'bar'] as const;
 *
 * // error
 * const hasThing = (t: string) => things.includes(t);
 *
 * // compiles
 * const hasThing = (t: string) => includes(things, t);
 *
 * @param array array to search
 * @param item item to search for
 */
export const includes = <T extends U, U>(
  array: ReadonlyArray<T>,
  item: U,
): item is T => array.includes(item as T);
